import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Wine, MapPin, CheckCircle, Gift, AlertCircle, Clock } from 'lucide-react';
import NearULogo from '../components/NearULogo';
import { QRCodeCanvas } from 'qrcode.react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RedeemDrinkPage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [drink, setDrink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [redeeming, setRedeeming] = useState(false);
  const [redeemed, setRedeemed] = useState(false);

  useEffect(() => {
    fetchDrinkDetails();
  }, [code]);

  const fetchDrinkDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/drinks/redeem/${code}`);
      setDrink(response.data);
      if (response.data.status === 'redeemed') {
        setRedeemed(true);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Drink not found');
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async () => {
    setRedeeming(true);
    try {
      await axios.post(`${API}/drinks/redeem/${code}`);
      setRedeemed(true);
      setDrink(prev => ({ ...prev, status: 'redeemed' }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to redeem drink');
    } finally {
      setRedeeming(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #0D0D0D 0%, #2A1B0E 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: '20px' }}><NearULogo size="medium" showText={true} /></div>
          <p style={{ color: '#A57C33', fontSize: '16px' }}>Loading drink details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #0D0D0D 0%, #2A1B0E 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ textAlign: 'center', maxWidth: '400px' }}>
          <div style={{ marginBottom: '20px' }}><NearULogo size="medium" showText={true} /></div>
          <div style={{
            width: '80px',
            height: '80px',
            background: 'rgba(220, 38, 38, 0.2)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <AlertCircle size={40} color="#fca5a5" />
          </div>
          <h2 style={{ fontSize: '24px', color: '#E6D7C3', marginBottom: '12px', fontFamily: 'Playfair Display, serif' }}>
            Drink Not Found
          </h2>
          <p style={{ color: '#A57C33', marginBottom: '24px' }}>{error}</p>
          <button
            onClick={() => navigate('/')}
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
              color: '#2C1B13',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #0D0D0D 0%, #2A1B0E 100%)', paddingBottom: '40px' }}>
      <div className="page-header">
        <div style={{ marginBottom: '12px' }}><NearULogo size="small" showText={true} /></div>
        <h1 className="page-title" style={{ fontFamily: 'Playfair Display, serif' }}>
          {redeemed ? 'DRINK REDEEMED!' : 'REDEEM DRINK'}
        </h1>
      </div>

      <div style={{ padding: '0 20px', maxWidth: '500px', margin: '0 auto' }}>
        {/* Status Banner */}
        {drink.status === 'pending' && (
          <div style={{
            background: 'rgba(234, 179, 8, 0.1)',
            border: '1px solid rgba(234, 179, 8, 0.3)',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Clock size={24} color="#eab308" />
            <div>
              <p style={{ color: '#eab308', fontWeight: 'bold', marginBottom: '4px' }}>Payment Pending</p>
              <p style={{ color: '#A57C33', fontSize: '14px' }}>This drink is still being processed</p>
            </div>
          </div>
        )}

        {redeemed && (
          <div style={{
            background: 'rgba(34, 197, 94, 0.1)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <CheckCircle size={24} color="#22c55e" />
            <div>
              <p style={{ color: '#22c55e', fontWeight: 'bold', marginBottom: '4px' }}>Already Redeemed</p>
              <p style={{ color: '#A57C33', fontSize: '14px' }}>This drink has been claimed</p>
            </div>
          </div>
        )}

        {/* Drink Details Card */}
        <div style={{
          background: 'rgba(230, 215, 195, 0.05)',
          border: '1px solid #A57C33',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px'
          }}>
            <Wine size={40} color="#2C1B13" />
          </div>

          <h2 style={{
            fontSize: '28px',
            color: '#E6D7C3',
            textAlign: 'center',
            marginBottom: '8px',
            fontFamily: 'Playfair Display, serif'
          }}>
            {drink.drink_name}
          </h2>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            marginBottom: '20px',
            color: '#A57C33'
          }}>
            <MapPin size={18} />
            <span style={{ fontSize: '16px' }}>{drink.venue_name}</span>
          </div>

          <div style={{
            textAlign: 'center',
            padding: '16px',
            background: 'rgba(215, 181, 109, 0.1)',
            borderRadius: '12px',
            marginBottom: '20px'
          }}>
            <p style={{ color: '#A57C33', fontSize: '14px', marginBottom: '4px' }}>Value</p>
            <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#D7B56D' }}>
              ${drink.drink_price.toFixed(2)}
            </p>
          </div>

          {drink.sender_name && (
            <div style={{
              padding: '16px',
              background: 'rgba(215, 181, 109, 0.1)',
              borderRadius: '12px',
              marginBottom: '12px'
            }}>
              <p style={{ color: '#A57C33', fontSize: '14px', marginBottom: '4px' }}>From</p>
              <p style={{ fontSize: '18px', color: '#E6D7C3', fontWeight: 'bold' }}>{drink.sender_name}</p>
            </div>
          )}

          {drink.personal_message && (
            <div style={{
              padding: '16px',
              background: 'rgba(215, 181, 109, 0.1)',
              borderRadius: '12px',
              marginBottom: '12px'
            }}>
              <p style={{ color: '#A57C33', fontSize: '14px', marginBottom: '8px' }}>Personal Message</p>
              <p style={{ fontSize: '16px', color: '#E6D7C3', fontStyle: 'italic', lineHeight: '1.5' }}>
                "{drink.personal_message}"
              </p>
            </div>
          )}

          {drink.custom_instructions && (
            <div style={{
              padding: '16px',
              background: 'rgba(215, 181, 109, 0.15)',
              border: '1px solid #D7B56D',
              borderRadius: '12px',
              marginBottom: '12px'
            }}>
              <p style={{ color: '#D7B56D', fontSize: '14px', marginBottom: '8px', fontWeight: 'bold' }}>🎨 Custom Instructions</p>
              <p style={{ fontSize: '16px', color: '#E6D7C3', lineHeight: '1.5' }}>
                {drink.custom_instructions}
              </p>
            </div>
          )}

          <div style={{
            padding: '12px',
            background: 'rgba(230, 215, 195, 0.05)',
            borderRadius: '8px',
            textAlign: 'center',
            borderTop: '1px solid #A57C33',
            marginTop: '16px'
          }}>
            <p style={{ color: '#8A7355', fontSize: '13px' }}>Redemption Code</p>
            <p style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: '#D7B56D',
              letterSpacing: '2px',
              marginTop: '4px'
            }}>
              {drink.unique_code}
            </p>
          </div>
        </div>

        {/* QR Code Display */}
        {!redeemed && drink.status === 'paid' && (
          <div style={{
            background: 'white',
            padding: '24px',
            borderRadius: '16px',
            textAlign: 'center',
            marginBottom: '24px'
          }}>
            <QRCodeCanvas
              value={window.location.href}
              size={200}
              level="H"
            />
            <p style={{ color: '#666', fontSize: '14px', marginTop: '12px' }}>
              Show this QR code to staff
            </p>
          </div>
        )}

        {/* Instructions */}
        <div style={{
          background: 'rgba(230, 215, 195, 0.05)',
          border: '1px solid #A57C33',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <h3 style={{ color: '#D7B56D', fontSize: '16px', fontWeight: 'bold', marginBottom: '12px' }}>
            How to Redeem
          </h3>
          <ol style={{ color: '#A57C33', fontSize: '14px', lineHeight: '1.8', paddingLeft: '20px' }}>
            <li>Show this screen or QR code to the staff at {drink.venue_name}</li>
            <li>Staff will verify your redemption code: {drink.unique_code}</li>
            <li>Enjoy your complimentary {drink.drink_name}!</li>
          </ol>
        </div>

        {/* Action Buttons */}
        {!redeemed && drink.status === 'paid' && (
          <button
            onClick={handleRedeem}
            disabled={redeeming}
            style={{
              width: '100%',
              padding: '16px',
              background: redeeming ? '#6B5635' : 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
              color: '#2C1B13',
              border: 'none',
              borderRadius: '12px',
              fontSize: '18px',
              fontWeight: 'bold',
              cursor: redeeming ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              marginBottom: '16px'
            }}
          >
            <Gift size={22} />
            {redeeming ? 'Redeeming...' : 'Mark as Redeemed'}
          </button>
        )}

        {drink.status === 'pending' && (
          <div style={{
            textAlign: 'center',
            padding: '20px',
            color: '#8A7355'
          }}>
            <p>This drink cannot be redeemed yet. Payment is still being processed.</p>
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <p style={{ color: '#8A7355', fontSize: '14px', marginBottom: '12px' }}>
            Want to send drinks to friends?
          </p>
          <button
            onClick={() => navigate('/')}
            style={{
              padding: '12px 24px',
              background: 'rgba(230, 215, 195, 0.1)',
              color: '#D7B56D',
              border: '1px solid #A57C33',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Explore NearU
          </button>
        </div>
      </div>
    </div>
  );
};

export default RedeemDrinkPage;
